/**
 * World save: the world is a pure function of the seed, so a save is only the
 * blocks the player changed (per chunk: voxel index -> block id) plus the
 * player state, the hotbar and the time of day. Stored in localStorage; edits
 * are handed to the generation worker so a reloaded chunk is lit with them in
 * place.
 */

import { WORLDGEN_VERSION } from '../worldgen/terrain';
import { worldKey } from './chunk';
import { deserializeExtras, serializeExtras, type GameMode, type Stack } from '../game/types';
import { type DimensionId, isDimensionId } from './dimension';

export interface SavedPlayer {
  x: number; y: number; z: number;
  yaw: number; pitch: number;
  flying: boolean;
}

/** Hotbar contents plus the selected slot, so the player's kit (and the block
 *  they were holding) survives a reload. `ids`/`counts` are indexed by slot. */
export interface SavedHotbar {
  ids: number[];
  counts: number[];
  selected: number;
}

interface SaveFile {
  player: SavedPlayer | null;
  time: number;
  hotbar?: SavedHotbar;
  /** "cx,cz" -> flat [index, id, index, id, ...] */
  edits: Record<string, number[]>;
  /** "x,y,z" -> flat [id, count, id, count, ...] chest contents (slot order). Legacy; read-only. */
  chests?: Record<string, number[]>;
  /** "x,y,z" -> flat [id, count, dmg, ...] chest contents, 27 stacks. */
  chestsV2?: Record<string, number[]>;
  /** Inventory.serialize(): 36 main + 4 armor + offhand, [id, count, dmg] each. */
  inventory?: number[];
  /** PlayerStats.serialize() blob. */
  stats?: unknown;
  gameMode?: GameMode;
  /** "x,y,z" -> FurnaceState.serialize(). */
  furnaces?: Record<string, number[]>;
  /** Point a bed or death set as the respawn position (always overworld, like vanilla beds). */
  spawn?: { x: number; y: number; z: number } | null;
  /** Dimension the player is in. Missing in old saves = overworld. */
  dimension?: DimensionId;
  /** Persistent world flags (villages / pillager outposts already populated). */
  flags?: string[];
  /** Per-dimension data for every dimension except the overworld, whose data
   *  stays at the top level (so pre-dimension saves load unchanged). */
  dims?: Partial<Record<DimensionId, DimFile>>;
  /** entities/persist.ts EntitySave blob per dimension. */
  entities?: Partial<Record<DimensionId, unknown>>;
  /** game/weather.ts WeatherSave blob. */
  weather?: unknown;
}

interface DimFile {
  edits: Record<string, number[]>;
  chestsV2?: Record<string, number[]>;
  furnaces?: Record<string, number[]>;
}

/** Block edits, chests and furnaces of one dimension. */
interface DimData {
  edits: Map<number, Map<number, number>>;
  coords: Map<number, string>;
  chests: Map<string, Stack[]>;
  furnaces: Record<string, number[]>;
}
const newDim = (): DimData => ({ edits: new Map(), coords: new Map(), chests: new Map(), furnaces: {} });

/** Autosave at most this often while there are unsaved changes. */
const AUTOSAVE_MS = 1000;

export class WorldSave {
  player: SavedPlayer | null = null;
  time: number | null = null;
  /** Hotbar contents + selected slot, or null until the host records them. */
  hotbar: SavedHotbar | null = null;
  /** Dimension the player is in; edits/chests/furnaces below are this dimension's. */
  dimension: DimensionId = 'overworld';
  private readonly dims: Record<DimensionId, DimData> = { overworld: newDim(), nether: newDim(), end: newDim() };
  /** Chest contents by "x,y,z": 27 stacks (current dimension). */
  get chests(): Map<string, Stack[]> { return this.dims[this.dimension].chests; }
  /** Inventory.serialize() output, or null until the host records it. */
  inventory: number[] | null = null;
  stats: unknown = null;
  gameMode: GameMode = 'survival';
  /** Furnace states of the current dimension. */
  get furnaces(): Record<string, number[]> { return this.dims[this.dimension].furnaces; }
  set furnaces(f: Record<string, number[]>) { this.dims[this.dimension].furnaces = f; }
  spawn: { x: number; y: number; z: number } | null = null;
  /** Persistent world flags (structure population markers and the like). */
  readonly flags = new Set<string>();
  /** Saved entities (entities/persist.ts blob) per dimension. */
  entities: Partial<Record<DimensionId, unknown>> = {};
  /** Weather state (game/weather.ts); written with the next save, never dirties it on its own. */
  weather: unknown = null;
  /** Snapshot of the current dimension's live entities, taken at every write. */
  entitySource: (() => unknown) | null = null;
  /** True when a save file existed at load (the title screen's "Continue"). */
  existed = false;
  private readonly storageKey: string;
  private get edits(): Map<number, Map<number, number>> { return this.dims[this.dimension].edits; }
  private get coords(): Map<number, string> { return this.dims[this.dimension].coords; }
  private dirty = false;
  private lastWrite = 0;

  constructor(seed: number) {
    // a new generator version produces a different world, so old edits no longer fit
    this.storageKey = `opencraft.world.v${WORLDGEN_VERSION}.${seed}`;
    this.load();
  }

  private load(): void {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return;
      this.existed = true;
      const file = JSON.parse(raw) as SaveFile;
      if (Array.isArray(file.inventory)) this.inventory = file.inventory;
      this.stats = file.stats ?? null;
      this.gameMode = file.gameMode === 'creative' ? 'creative' : 'survival';
      this.dims.overworld.furnaces = file.furnaces ?? {};
      this.spawn = file.spawn ?? null;
      this.player = file.player ?? null;
      for (const f of file.flags ?? []) this.flags.add(f);
      if (file.entities && typeof file.entities === 'object') this.entities = file.entities;
      this.weather = file.weather ?? null;
      this.time = typeof file.time === 'number' ? file.time : null;
      const h = file.hotbar;
      if (h && Array.isArray(h.ids) && Array.isArray(h.counts)) {
        this.hotbar = { ids: h.ids, counts: h.counts, selected: h.selected | 0 };
      }
      readEdits(this.dims.overworld, file.edits);
      readChestsV2(this.dims.overworld, file.chestsV2);
      for (const id of ['nether', 'end'] as const) {
        const d = file.dims?.[id];
        if (!d) continue;
        readEdits(this.dims[id], d.edits);
        readChestsV2(this.dims[id], d.chestsV2);
        this.dims[id].furnaces = d.furnaces ?? {};
      }
      this.dimension = isDimensionId(file.dimension) ? file.dimension : 'overworld';
      for (const [key, flat] of Object.entries(file.chests ?? {})) {
        if (!Array.isArray(flat)) continue;
        const stacks: Stack[] = [];
        for (let i = 0; i < 27; i++) {
          const id = flat[i * 2] | 0, count = flat[i * 2 + 1] | 0;
          stacks.push(id > 0 && count > 0 ? { id, count, dmg: 0 } : { id: 0, count: 0, dmg: 0 });
        }
        this.dims.overworld.chests.set(key, stacks);
      }
    } catch {
      // unreadable save: start fresh rather than fail to boot
    }
  }

  /** Chest contents at a world position (27 stacks, mutated in place by the UI). */
  chest(key: string): Stack[] | undefined {
    return this.chests.get(key);
  }

  recordChest(key: string, contents: Stack[]): void {
    this.chests.set(key, contents);
    this.dirty = true;
  }

  /** Flag unsaved changes made to data held by reference (chest stacks, furnaces). */
  touch(): void { this.dirty = true; }

  setInventory(flat: number[]): void { this.inventory = flat; this.dirty = true; }
  setStats(stats: unknown): void { this.stats = stats; this.dirty = true; }
  setGameMode(m: GameMode): void { this.gameMode = m; this.dirty = true; }
  setFurnaces(f: Record<string, number[]>): void { this.furnaces = f; this.dirty = true; }
  setSpawn(p: { x: number; y: number; z: number } | null): void { this.spawn = p; this.dirty = true; }

  /** Persistent world flags (VillageSystem population markers). */
  hasFlag(key: string): boolean { return this.flags.has(key); }
  setFlag(key: string): void { if (!this.flags.has(key)) { this.flags.add(key); this.dirty = true; } }

  /** Drop a chest's record (it was broken or moved): vanilla gives the items
   *  back to the world, and a chest placed at the same spot starts empty. */
  forgetChest(key: string): void {
    if (this.chests.delete(key)) this.dirty = true;
  }

  /** Make `dim` the current dimension (edits/chests/furnaces now refer to it). */
  setDimension(dim: DimensionId): void {
    if (dim === this.dimension) return;
    this.dimension = dim;
    this.dirty = true;
  }

  /** Edits of one chunk in a specific dimension (smoke tests, portal search). */
  editsIn(dim: DimensionId, cx: number, cz: number): Map<number, number> | undefined {
    return this.dims[dim].edits.get(worldKey(cx, cz));
  }

  /** Remember that the block at voxel `index` of chunk (cx, cz) is now `id`. */
  record(cx: number, cz: number, index: number, id: number): void {
    const key = worldKey(cx, cz);
    let m = this.edits.get(key);
    if (!m) {
      m = new Map();
      this.edits.set(key, m);
      this.coords.set(key, cx + ',' + cz);
    }
    m.set(index, id);
    this.dirty = true;
  }

  /** Edits of one chunk as flat [index, id, ...] pairs for the gen worker. */
  editsFor(cx: number, cz: number): Int32Array | undefined {
    const m = this.edits.get(worldKey(cx, cz));
    if (!m || m.size === 0) return undefined;
    const out = new Int32Array(m.size * 2);
    let i = 0;
    for (const [index, id] of m) {
      out[i++] = index;
      out[i++] = id;
    }
    return out;
  }

  setPlayer(player: SavedPlayer, time: number): void {
    this.player = player;
    this.time = time;
    this.dirty = true;
  }

  /** Remember the hotbar (contents + selected slot). Called on every hotbar
   *  change, so it must stay cheap — the write itself is still throttled. */
  setHotbar(hotbar: SavedHotbar): void {
    this.hotbar = hotbar;
    this.dirty = true;
  }

  /** Write if something changed and the autosave interval has passed (or `force`). */
  flush(force = false): void {
    if (!this.dirty) return;
    const now = performance.now();
    if (!force && now - this.lastWrite < AUTOSAVE_MS) return;
    this.lastWrite = now;
    if (this.entitySource) {
      try { this.entities[this.dimension] = this.entitySource(); } catch { /* keep the last snapshot */ }
    }
    const ow = writeDim(this.dims.overworld);
    const file: SaveFile = {
      player: this.player, time: this.time ?? 0.25, hotbar: this.hotbar ?? undefined,
      edits: ow.edits, chestsV2: ow.chestsV2, inventory: this.inventory ?? undefined, stats: this.stats ?? undefined,
      gameMode: this.gameMode, furnaces: ow.furnaces, spawn: this.spawn,
      dimension: this.dimension, flags: this.flags.size ? [...this.flags] : undefined,
      dims: { nether: writeDim(this.dims.nether), end: writeDim(this.dims.end) },
      entities: this.entities, weather: this.weather ?? undefined,
    };
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(file));
      this.dirty = false;
    } catch {
      // storage full or unavailable: keep playing, retry on the next flush
    }
  }

  /** Delete the save; the next load regenerates the pristine world. */
  reset(): void {
    this.dims.overworld = newDim();
    this.dims.nether = newDim();
    this.dims.end = newDim();
    this.dimension = 'overworld';
    this.player = null;
    this.hotbar = null;
    this.inventory = null;
    this.stats = null;
    this.spawn = null;
    this.entities = {};
    this.weather = null;
    this.flags.clear();
    this.existed = false;
    this.dirty = false;
    try { localStorage.removeItem(this.storageKey); } catch { /* unavailable */ }
  }
}

function readEdits(d: DimData, edits: Record<string, number[]> | undefined): void {
  for (const [coord, flat] of Object.entries(edits ?? {})) {
    if (!Array.isArray(flat)) continue;
    const [cx, cz] = coord.split(',').map(Number);
    const m = new Map<number, number>();
    for (let i = 0; i + 1 < flat.length; i += 2) m.set(flat[i], flat[i + 1]);
    const key = worldKey(cx, cz);
    d.edits.set(key, m);
    d.coords.set(key, coord);
  }
}

function readChestsV2(d: DimData, chests: Record<string, number[]> | undefined): void {
  for (const [key, flat] of Object.entries(chests ?? {})) {
    if (!Array.isArray(flat)) continue;
    const stacks: Stack[] = [];
    for (let i = 0; i < 27; i++) {
      const id = flat[i * 3] | 0, count = flat[i * 3 + 1] | 0, dmg = flat[i * 3 + 2] | 0;
      stacks.push(id > 0 && count > 0 ? { id, count, dmg } : { id: 0, count: 0, dmg: 0 });
    }
    deserializeExtras(stacks, flat, 27 * 3);
    d.chests.set(key, stacks);
  }
}

function writeDim(d: DimData): DimFile & { chestsV2: Record<string, number[]> } {
  const edits: Record<string, number[]> = {};
  for (const [key, m] of d.edits) {
    const flat: number[] = [];
    for (const [index, id] of m) flat.push(index, id);
    edits[d.coords.get(key)!] = flat;
  }
  const chestsV2: Record<string, number[]> = {};
  for (const [key, stacks] of d.chests) {
    const flat = stacks.flatMap((s) => [s.id, s.count, s.dmg]);
    serializeExtras(stacks, flat);
    chestsV2[key] = flat;
  }
  return { edits, chestsV2, furnaces: d.furnaces };
}
